import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const accountType = searchParams.get('type') || 'client';
  const isCoach = accountType === 'coach';
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await API.post('/auth/forgot-password', { email, account_type: accountType });
      setSubmitted(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F0E9] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 mb-12 justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
            alt="Quiet Mind Golf" 
            className="h-12 w-auto"
          />
          <span className="font-['Playfair_Display'] text-xl font-medium text-[#1A1A1A]">
            Quiet Mind Golf
          </span>
        </Link>

        {submitted ? (
          <div className="bg-white border border-[#E7E5E4] p-8 text-center" data-testid="reset-email-sent">
            <CheckCircle className="w-16 h-16 text-[#2C5530] mx-auto mb-6" />
            <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-4">
              Check Your Email
            </h1>
            <p className="text-[#57534E] mb-6">
              If an account exists with <strong>{email}</strong>, we've sent a password reset link.
            </p>
            <p className="text-[#737373] text-sm mb-6">
              The link will expire in 1 hour. Check your spam folder if you don't see it.
            </p>
            <Link
              to={isCoach ? "/coach-login" : "/login"}
              className="inline-flex items-center gap-2 text-[#2C5530] font-semibold hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {isCoach ? 'Coach' : ''} Login
            </Link>
          </div>
        ) : (
          <div className="bg-white border border-[#E7E5E4] p-8">
            <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">
              Forgot Password?
            </h1>
            <p className="text-[#57534E] mb-6">
              Enter your email and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300 disabled:opacity-50"
                data-testid="send-reset-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to={isCoach ? "/coach-login" : "/login"}
                className="inline-flex items-center gap-2 text-[#737373] hover:text-[#1A1A1A] text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to {isCoach ? 'Coach' : ''} Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
